import useReportSort from '../../components/reports/useReportSort';
import { useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiDownload,
  FiFileText,
  FiFilter,
  FiLoader,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiUsers,
} from "react-icons/fi";

import ReportFilterModal from "../../components/reports/ReportFilterModal";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";

const dateOnly = (value) => new Date(value).toISOString().slice(0, 10);
const firstDay = () => {
  const value = new Date();
  value.setDate(1);
  return dateOnly(value);
};
const initialFilters = () => ({
  from: firstDay(),
  to: dateOnly(new Date()),
  search: "",
});
const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const quantity = (value) =>
  Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

function downloadFile(blob, name) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

function Field({ label, children }) {
  return (
    <label className="block text-sm font-semibold text-slate-700">
      <span className="mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

export default function AgentWiseSalesReportPage() {
  const { accessToken, user } = useAuth();
  const canGenerate = hasPermission(user, "reports.agent_wise_sales.generate");
  const canExport = hasPermission(user, "reports.agent_wise_sales.export");
  const [rawReport, setReport] = useState(null);
  const { report, controls: sortControls } = useReportSort(rawReport);
  const [showFilters, setShowFilters] = useState(false);
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const rows = report?.rows || [];
  const visibleRows = useMemo(() => {
    const search = appliedFilters.search.trim().toLowerCase();
    if (!search) return rows;

    return rows.filter((row) =>
      [
        row.agent,
        row.billNumber,
        row.productName,
        row.productCode,
        row.sku,
        row.barcode,
        row.color,
        row.size,
        row.store,
        row.counter,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search)),
    );
  }, [appliedFilters.search, rows]);

  const summary = useMemo(
    () => ({
      agents: new Set(visibleRows.map((row) => row.agentCode)).size,
      bills: new Set(visibleRows.map((row) => row.billNumber)).size,
      soldQty: visibleRows.reduce(
        (sum, row) => sum + Number(row.quantity || 0),
        0,
      ),
      returnQty: visibleRows.reduce(
        (sum, row) => sum + Number(row.returnQty || 0),
        0,
      ),
      netSales: visibleRows.reduce(
        (sum, row) => sum + Number(row.netSales || 0),
        0,
      ),
    }),
    [visibleRows],
  );

  const updateFilter = (key, value) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  const generate = async () => {
    if (!canGenerate) return;
    setLoading(true);
    setError("");

    try {
      const nextReport = await apiRequest(
        "/business-reports/agent-wise-sales/generate",
        {
          method: "POST",
          body: { from: draftFilters.from, to: draftFilters.to },
          accessToken,
        },
      );
      setReport(nextReport);
      setAppliedFilters({ ...draftFilters });
      setShowFilters(false);
    } catch (requestError) {
      setReport(null);
      setError(requestError.message || "Could not generate agent sales.");
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    if (!report || !canExport) return;
    setExporting(true);
    setError("");

    try {
      const params = new URLSearchParams({
        from: appliedFilters.from,
        to: appliedFilters.to,
        format: "XLSX",
      });
      const response = await apiRequest(
        `/business-reports/agent-wise-sales/export?${params.toString()}`,
        { accessToken, rawResponse: true },
      );
      const name =
        response.headers
          .get("content-disposition")
          ?.match(/filename="?([^";]+)"?/)?.[1] ||
        "charms-agent-wise-sales.xlsx";
      downloadFile(await response.blob(), name);
    } catch (requestError) {
      setError(requestError.message || "Could not export agent sales.");
    } finally {
      setExporting(false);
    }
  };

  const openFilters = () => {
    setDraftFilters({ ...appliedFilters });
    setShowFilters(true);
  };

  const closeFilters = () => {
    setDraftFilters({ ...appliedFilters });
    setShowFilters(false);
  };

  const columns = [
    "Agent name",
    "Bill date",
    "Bill no.",
    "Company",
    "Product",
    "Product code",
    "Category",
    "SKU",
    "Barcode",
    "Variant",
    "Colour",
    "Size",
    "Sold qty",
    "Return qty",
    "Net qty",
    "MRP",
    "Sale rate",
    "Discount",
    "GST",
    "GST amount",
    "Net sales",
    "Counter",
    "Store",
  ];

  return (
    <div className="business-report space-y-5">
      <section className="border-b border-slate-200 bg-white px-1 pb-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[.18em] text-primary-800">
              <FiUsers /> Business reports
            </div>
            <h1 className="text-3xl font-bold text-slate-950">
              Agent-wise Sales Report
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-500">
              Every POS bill line assigned to an agent, with complete product
              details. Customer information is not shown in this report.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openFilters}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-primary-300 hover:text-primary-800"
            >
              <FiFilter /> Filters
            </button>
            <button
              type="button"
              onClick={generate}
              disabled={!canGenerate || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiRefreshCw />
              )}
              {loading ? "Generating…" : "Generate"}
            </button>
            <button
              type="button"
              onClick={exportExcel}
              disabled={!report || !canExport || exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-primary-300 hover:text-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporting ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiDownload />
              )}{" "}
              Export Excel
            </button>
          </div>
        </div>
      </section>

      {error && (
        <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}
      {!canGenerate && (
        <p className="flex gap-2 rounded-lg bg-amber-50 p-4 text-sm text-amber-800">
          <FiShield className="mt-0.5 shrink-0" />
          You do not have permission to generate this report.
        </p>
      )}

      {!report ? (
        <section className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-20 text-center">
          <FiFileText className="mx-auto mb-4 text-4xl text-primary-300" />
          <h2 className="text-xl font-bold text-slate-900">
            No preview generated yet
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Choose a date range, then select Generate.
          </p>
        </section>
      ) : (
        <>
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {[
              ["Agents", summary.agents, true, "text-primary-800"],
              ["Bills", summary.bills, true, "text-sky-700"],
              ["Sold quantity", summary.soldQty, true, "text-emerald-700"],
              ["Return quantity", summary.returnQty, true, "text-amber-700"],
              ["Net sales", summary.netSales, false, "text-violet-700"],
            ].map(([label, value, isQuantity, color]) => (
              <div
                key={label}
                className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <p className="text-sm text-slate-500">{label}</p>
                <p className={`mt-2 text-2xl font-bold ${color}`}>
                  {isQuantity ? quantity(value) : money(value)}
                </p>
              </div>
            ))}
          </section>

          <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-2 border-b border-slate-100 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-950">
                  Agent bill and product details
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {visibleRows.length.toLocaleString("en-IN")} product line(s),
                  with agent name first and no customer data.
                </p>
              </div>
              <p className="text-xs font-medium text-slate-500">
                {appliedFilters.from} to {appliedFilters.to}
              </p>
            </div>
            <div className="overflow-x-auto">
                  {sortControls}
              <table className="min-w-[2600px] w-full text-sm">
                <thead className="bg-slate-100 text-left text-xs font-bold uppercase tracking-wide text-slate-600">
                  <tr>
                    {columns.map((header) => (
                      <th
                        key={header}
                        className="whitespace-nowrap border-b border-slate-200 px-4 py-3"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-slate-100 text-slate-700 hover:bg-primary-50/30"
                    >
                      <td className="sticky left-0 bg-white px-4 py-3 font-semibold text-primary-900">
                        {row.agent}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {new Date(row.billDate).toLocaleDateString("en-IN")}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs font-semibold">
                        {row.billNumber}
                      </td>
                      <td className="px-4 py-3">{row.companyName || "—"}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900">
                        {row.productName}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {row.productCode || "—"}
                      </td>
                      <td className="px-4 py-3">{row.category || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs">{row.sku}</td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {row.barcode || "—"}
                      </td>
                      <td className="px-4 py-3">{row.variant || "—"}</td>
                      <td className="px-4 py-3">{row.color || "—"}</td>
                      <td className="px-4 py-3">{row.size || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        {quantity(row.quantity)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {quantity(row.returnQty)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {quantity(row.netQuantity)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {row.listPrice === null ? "—" : money(row.listPrice)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {money(row.unitPrice)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {money(row.discount)}
                      </td>
                      <td className="px-4 py-3 text-right">{row.taxRate}%</td>
                      <td className="px-4 py-3 text-right">{money(row.tax)}</td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-700">
                        {money(row.netSales)}
                      </td>
                      <td className="px-4 py-3">{row.counter}</td>
                      <td className="px-4 py-3">{row.store}</td>
                    </tr>
                  ))}
                  {!visibleRows.length && (
                    <tr>
                      <td
                        colSpan="23"
                        className="px-4 py-16 text-center text-sm text-slate-500"
                      >
                        No agent-assigned sales match the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      <ReportFilterModal
        open={showFilters}
        title="Agent-wise sales filters"
        description="Choose the date range and optionally search an agent, bill, product, barcode or store. Generate applies the filters and closes this window."
        loading={loading}
        onApply={generate}
        onClose={closeFilters}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="From date">
            <input
              type="date"
              value={draftFilters.from}
              onChange={(event) => updateFilter("from", event.target.value)}
              className="report-input"
            />
          </Field>
          <Field label="To date">
            <input
              type="date"
              value={draftFilters.to}
              onChange={(event) => updateFilter("to", event.target.value)}
              className="report-input"
            />
          </Field>
          <Field label="Search agent, bill or product">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={draftFilters.search}
                onChange={(event) => updateFilter("search", event.target.value)}
                className="report-input pl-9"
                placeholder="Agent, bill, product, barcode or store"
              />
            </div>
          </Field>
        </div>
      </ReportFilterModal>
    </div>
  );
}
